import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { EntityDefinition, Tenant, WritePolicy } from '../api';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { ErrorNotice } from '../components/ErrorNotice';
import { tenantLinks } from '../tenantLinks.js';
import { WriteBlockedNotice } from '../components/WriteBlockedNotice.js';
import { hasAnyQueryKey, stripQueryKeys } from '../stripQueryKeys';

/** One-shot flags the Acumatica OAuth callback puts on this page's URL. */
const OAUTH_RESULT_KEYS = ['connected', 'connectionError'] as const;

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [entities, setEntities] = useState<EntityDefinition[]>([]);
  const [policy, setPolicy] = useState<WritePolicy | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnectResult, setDisconnectResult] = useState<string | null>(null);
  // Empty until the instance is asked, and it is only asked while the edit
  // form is open — the probe costs a metered read from the customer's API
  // budget, and nothing outside the form has any use for the answer.
  const [endpointVersions, setEndpointVersions] = useState<string[]>([]);
  const [form, setForm] = useState({
    companyName: '',
    instanceBaseUrl: '',
    endpointVersion: '',
    clientId: '',
    clientSecret: '',
  });

  // The OAuth callback redirects back here with one of these set, because a
  // browser redirect is the only channel it has to report an outcome.
  // Read ONCE into state, then stripped from the URL below. The values still
  // drive the notices for this visit; what goes away is the claim in the
  // address bar that they just happened.
  const [justConnected] = useState(() => searchParams.get('connected') === '1');
  const [callbackError] = useState(() => searchParams.get('connectionError'));

  // ⚠ Stripped after reading, not before: these flags report an OUTCOME, and
  // the notice is the only thing that consumes them. Left in place, a refresh
  // re-showed "just connected" and a shared link claimed it for whoever opened
  // it. `replace` so the back button cannot return to the flagged URL.
  useEffect(() => {
    if (!hasAnyQueryKey(searchParams, OAUTH_RESULT_KEYS)) return;
    setSearchParams(stripQueryKeys(searchParams, OAUTH_RESULT_KEYS), { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    if (!api || !tenantId) return;
    try {
      const result = await api.get<{ tenant: Tenant }>(`/tenants/${tenantId}`);
      setTenant(result.tenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant.');
    }
  }, [api, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Driven by the API rather than hardcoded, so an entity added on the server
  // appears here without a front-end change - and so this page never offers a
  // link to something the server cannot actually read.
  useEffect(() => {
    if (!api) return;
    api
      .get<{ entities: EntityDefinition[] }>('/entities')
      .then(({ entities: supported }) => setEntities(supported))
      .catch(() => setEntities([]));
  }, [api]);

  // Whether this instance can write anything, asked on the page a customer
  // lands on straight after authorizing. It reads our own database only — no
  // Acumatica call, so it costs the customer nothing from their API budget.
  //
  // Silent on failure on purpose: this notice exists to explain a state, and a
  // page that cannot reach the API has a louder problem to report than that.
  useEffect(() => {
    if (!api || !tenantId) return;
    api
      .get<WritePolicy>(`/tenants/${tenantId}/write-policy`)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [api, tenantId]);

  async function handleTestConnection() {
    if (!api || !tenantId || testing) return;
    setTesting(true);
    setError(null);

    try {
      await api.post(`/tenants/${tenantId}/test-connection`);
      // Re-read rather than trusting the response body: the endpoint also
      // writes the result to the tenant row, and the row is the thing the rest
      // of the UI keys off.
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  }

  // The instance was declared a sandbox, but something in it changed within
  // the last day — which is what a live system looks like. Not proof, and
  // deliberately not treated as proof: it is grounds to ask once, before any
  // write happens. Silenced permanently once answered either way.
  const contradictsSandbox =
    tenant?.instanceKind === 'sandbox' &&
    tenant.activitySeenRecently === true &&
    tenant.instanceKindConfirmedAt === null;

  async function answerChallenge(instanceKind: 'production' | 'sandbox') {
    if (!api || !tenantId) return;
    setError(null);
    try {
      await api.post(`/tenants/${tenantId}/instance-kind`, { instanceKind });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the instance type.');
    }
  }

  function startEditing() {
    if (!tenant) return;
    setEndpointVersions([]);
    // Fired and not awaited: the form opens immediately with the saved value,
    // and the list upgrades the field in place when it arrives. Waiting would
    // make the button feel broken on a slow or unreachable instance, which is
    // precisely the instance someone is opening this form to repair.
    void api
      ?.get<{ versions: string[] }>(`/tenants/${tenantId}/endpoint-versions`)
      .then(({ versions }) => setEndpointVersions(versions))
      .catch(() => setEndpointVersions([]));
    setForm({
      companyName: tenant.companyName,
      instanceBaseUrl: tenant.instanceBaseUrl,
      endpointVersion: tenant.endpointVersion,
      clientId: tenant.clientId,
      // Never pre-filled: the stored secret is not sent to the browser, and an
      // empty box correctly means "leave the saved one alone".
      clientSecret: '',
    });
    setError(null);
    setEditing(true);
  }

  async function saveEdits(event: FormEvent) {
    event.preventDefault();
    if (!api || !tenantId || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/tenants/${tenantId}`, {
        companyName: form.companyName,
        instanceBaseUrl: form.instanceBaseUrl,
        // Always null — see acumatica/client.ts. The header this fed is gone,
        // proven inert against the live instance.
        acumaticaCompany: null,
        endpointVersion: form.endpointVersion,
        clientId: form.clientId,
        ...(form.clientSecret ? { clientSecret: form.clientSecret } : {}),
      });
      setEditing(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!api || !tenantId || disconnecting) return;
    setDisconnecting(true);
    setError(null);
    try {
      const response = await api.post<{ note: string }>(`/tenants/${tenantId}/disconnect`, {});
      setDisconnectResult(response.note);
      setConfirmDisconnect(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disconnect this instance.');
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleReconnect() {
    if (!api || !tenantId) return;
    try {
      const { authorizeUrl } = await api.get<{ authorizeUrl: string }>(
        `/tenants/${tenantId}/oauth/authorize`
      );
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start authorization.');
    }
  }

  if (!tenant) {
    return (
      <>
        <ErrorNotice message={error} />
        {!error && <p className="muted">Loading…</p>}
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/tenants" className="muted small">
            ← All instances
          </Link>
          <h1>{tenant.companyName}</h1>
        </div>
        {/*
          Edit is available WHETHER OR NOT the connection works, and that is the
          whole point. This block used to be gated on connectionStatus ===
          'connected', which meant a connection could only be corrected once it
          already worked — so a wrong endpoint version, instance URL or client id
          entered at onboarding left the customer with no way to fix it. That is
          the incident in CLAUDE.md that was "repaired by hand-editing the SQL
          row", and it happened again on September 13, 2026: this instance
          offers five Default endpoint versions and the wrong one was chosen.

          The data screens stay gated, because there is genuinely nothing to show
          until the instance is reachable.
        */}
        <div className="toolbar">
          <button type="button" onClick={startEditing}>
            Edit connection
          </button>
          {tenantLinks(tenant.tenantId, tenant.connectionStatus, entities).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={link.primary ? 'primary button-link' : 'button-link'}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {disconnectResult && <div className="notice success">{disconnectResult}</div>}

      {tenant.disconnectedAt && (
        <div className="notice warn">
          <strong>This instance is disconnected.</strong> It is not being
          billed. Its job history is kept for a short period — authorize in
          Acumatica again to start using it, and anything still inside that
          period will still be there.
        </div>
      )}

      {justConnected && (
        <div className="notice success">Connected to Acumatica successfully.</div>
      )}

      {/*
        ⚠ THE NEXT STEP, SAID ON THE PAGE THAT FOLLOWS AUTHORIZING. A customer
        who signed up and connected an instance used to be shown nothing about
        paying — no notice, no prompt — until a write was refused inside the
        grid's preview panel, after the work. The one link to billing was in
        that same panel, so the path to becoming a paying customer ran through
        first being told no.
      */}
      <WriteBlockedNotice policy={policy} tenantId={tenant.tenantId} place="instance" />

      {contradictsSandbox && (
        <div className="notice warn">
          <strong>Is this really a sandbox?</strong> You marked this instance as a
          sandbox, but a customer record in it changed within the last day — which
          is what a system people are actually working in looks like. Writing to
          the wrong instance is the mistake worth catching before it happens, and
          we have no way to tell a sandbox from production on our own.
          <div className="form-actions">
            <button type="button" onClick={() => void answerChallenge('sandbox')}>
              Yes, it is a sandbox
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void answerChallenge('production')}
            >
              It is actually production
            </button>
          </div>
        </div>
      )}
      {/*
        A grant that is about to lapse, said out loud BEFORE a job fails on it.
        Acumatica expiration is absolute by default: it runs from the moment
        the customer authorized, and using the connection does not extend it.
        Nothing in the product notices that until a write fails, at which point
        it looks like a fault rather than routine housekeeping.

        Worded as an expectation on purpose. We cannot read the customer's
        expiration setting, so this is a prediction from Acumatica's default —
        and `longer-than-expected` (which renders nothing here) is the state
        where a tenant has already disproved it.
      */}
      {tenant.refreshToken?.state === 'expiring' && (
        <div className="notice warn">
          <strong>
            {tenant.refreshToken.daysRemaining !== null &&
            tenant.refreshToken.daysRemaining <= 0
              ? 'This connection needs re-authorizing today.'
              : `This connection needs re-authorizing within ${tenant.refreshToken.daysRemaining} days.`}
          </strong>{' '}
          Acumatica authorizations expire {tenant.refreshToken.lifetimeDays} days
          after they are granted, and using the connection does not extend that.
          Re-authorizing takes a few seconds — use “Re-authorize in Acumatica”
          below, any time before it lapses. If your Acumatica administrator set a
          longer or sliding expiration, this may not apply.
        </div>
      )}

      {tenant.refreshToken?.state === 'expired' && (
        <div className="notice warn">
          <strong>This authorization has most likely expired.</strong> It was
          granted more than {tenant.refreshToken.lifetimeDays} days ago, and
          Acumatica expires grants on a fixed clock from that moment. If the grid
          or a job starts failing, re-authorize below — this is normal Acumatica
          behavior and does not mean anything is wrong with your instance.
        </div>
      )}

      <ErrorNotice message={callbackError ?? error} />

      {editing && (
        <form className="card" onSubmit={(e) => void saveEdits(e)}>
          <h2>Edit connection</h2>
          <p className="muted small">
            Changing the instance URL, the client ID or the secret means the
            saved authorization no longer matches what it points at, so the
            connection returns to pending and needs authorizing again. Editing
            only the name or endpoint version leaves it connected.
          </p>

          <label>
            <span>Company name</span>
            <input
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              required
              maxLength={200}
            />
            <small className="muted">How this instance is labeled in AcuTidy.</small>
          </label>

          <label>
            <span>Instance base URL</span>
            <input
              value={form.instanceBaseUrl}
              onChange={(e) => setForm({ ...form, instanceBaseUrl: e.target.value })}
              className="mono"
              required
              maxLength={500}
            />
            <small className="muted">
              The address you sign in to Acumatica at — not AcuTidy's redirect URI.
            </small>
          </label>

          {/*
            A dropdown once the instance has told us what it publishes, and a
            text box until then.

            This field is the reason the form exists. One right answer, no way
            to guess it, and Acumatica's response to a wrong one is an HTTP 500
            that never mentions the version — so a customer who mistypes it has
            no path back on their own. The instance publishes the list; asking
            it beats asking them.

            The saved value is kept as an option even when it is NOT in the
            list. A select whose value matches no option renders blank, and
            saving from there would silently overwrite the stored version with
            something the customer never chose.
          */}
          <label>
            <span>Default endpoint version</span>
            {endpointVersions.length > 0 ? (
              <select
                value={form.endpointVersion}
                onChange={(e) => setForm({ ...form, endpointVersion: e.target.value })}
                className="mono"
              >
                {!endpointVersions.includes(form.endpointVersion) && (
                  <option value={form.endpointVersion}>
                    {form.endpointVersion} — not published by this instance
                  </option>
                )}
                {endpointVersions.map((version) => (
                  <option key={version} value={version}>
                    {version}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={form.endpointVersion}
                onChange={(e) => setForm({ ...form, endpointVersion: e.target.value })}
                className="mono"
                placeholder="detected when you authorize"
                maxLength={50}
              />
            )}
            <small className="muted">
              {endpointVersions.length > 0
                ? 'These are the Default endpoint versions this instance publishes.'
                : 'Leave this blank — AcuTidy reads it from your instance when you authorize, and offers the real list here afterwards. Only set it by hand to pin an older Default endpoint deliberately.'}
            </small>
          </label>

          <label>
            <span>Client ID</span>
            <input
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              className="mono"
              required
              maxLength={200}
            />
          </label>

          <label>
            <span>
              Client secret <span className="muted">(leave blank to keep the saved one)</span>
            </span>
            <input
              value={form.clientSecret}
              onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
              type="password"
              className="mono"
              autoComplete="off"
              maxLength={500}
            />
            <small className="muted">
              The saved secret is never sent back to this browser, so this box
              starts empty. Fill it only to replace the secret.
            </small>
          </label>

          <div className="form-actions">
            <button type="button" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      <div className="card">
        <dl className="detail-grid">
          <dt>Status</dt>
          <dd>
            <ConnectionBadge
              status={tenant.connectionStatus}
              checkedAt={tenant.connectionCheckedAt}
            />
          </dd>

          <dt>Instance type</dt>
          <dd>
            {tenant.instanceKind === 'sandbox' ? 'Sandbox' : 'Production'}
            <span className="muted small">
              {' '}
              {tenant.instanceKind === 'sandbox'
                ? '— free when paired with a production instance'
                : '— $199/month per production and sandbox pair'}
            </span>
          </dd>

          <dt>Instance</dt>
          <dd className="mono">{tenant.instanceBaseUrl}</dd>

          <dt>Endpoint version</dt>
          <dd className={tenant.endpointVersion ? 'mono' : undefined}>
            {tenant.endpointVersion || (
              <span className="muted">
                — <span className="small">read from your instance when you authorize</span>
              </span>
            )}
          </dd>

          <dt>Authorization</dt>
          <dd>
            {tenant.refreshToken && tenant.refreshToken.state !== 'unknown' ? (
              <>
                granted{' '}
                {tenant.refreshToken.issuedAt
                  ? new Date(tenant.refreshToken.issuedAt).toLocaleDateString()
                  : '—'}
                {tenant.refreshToken.state === 'longer-than-expected' ? (
                  <span className="muted small">
                    {' '}
                    — still working past the expected{' '}
                    {tenant.refreshToken.lifetimeDays}-day window, so this instance
                    is set to a longer or sliding expiration
                  </span>
                ) : (
                  <span className="muted small">
                    {' '}
                    — expected to lapse{' '}
                    {tenant.refreshToken.expiresAt
                      ? new Date(tenant.refreshToken.expiresAt).toLocaleDateString()
                      : '—'}
                  </span>
                )}
              </>
            ) : (
              <span className="muted">
                —{' '}
                <span className="small">
                  recorded from the next time this instance is authorized
                </span>
              </span>
            )}
          </dd>

          <dt>Client ID</dt>
          <dd className="mono">{tenant.clientId}</dd>
        </dl>

        {tenant.connectionError && (
          <div className="notice error">
            <strong>Last error:</strong> {tenant.connectionError}
          </div>
        )}

        {/*
          Disconnecting is the only way to stop paying for ONE instance
          without cancelling everything. Before it existed,
          HasConnectedInstance was only ever set to 1, so a pair that had
          once connected was billable forever.

          The confirmation says what goes immediately and what does not. The
          data is deliberately NOT deleted now: this is one button whose
          obvious mistake is picking the wrong one of two instances, and
          cancelling a subscription - the far more deliberate act - gets
          weeks of grace. The casual action should not get less.
        */}
        {confirmDisconnect && (
          <div className="notice warn">
            <strong>Disconnect {tenant.companyName}?</strong>
            <ul>
              <li>
                The credentials linking AcuTidy to this instance are deleted
                straight away. AcuTidy loses all access to it.
              </li>
              <li>
                It stops counting towards your bill from your next invoice.
                The rest of the period you have already paid for is not
                refunded.
              </li>
              <li>
                Its job history and undo log are kept for a short period and
                then deleted. Reconnect the same instance before then and they
                are still there.
              </li>
            </ul>
            You will need to authorize again in Acumatica to use this instance
            with AcuTidy afterwards.
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setConfirmDisconnect(false)}
                disabled={disconnecting}
              >
                Keep it connected
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => void handleDisconnect()}
                disabled={disconnecting}
              >
                {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </button>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => void handleTestConnection()}
            disabled={testing}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button type="button" onClick={() => void handleReconnect()}>
            {tenant.connectionStatus === 'connected'
              ? 'Re-authorize in Acumatica'
              : 'Authorize in Acumatica'}
          </button>
          {!tenant.disconnectedAt && !confirmDisconnect && (
            <button type="button" onClick={() => setConfirmDisconnect(true)}>
              Disconnect
            </button>
          )}
        </div>
      </div>
    </>
  );
}
